import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { IZone } from "@nju/data-access-portal-dashboard";
import { useMemberOrchestrator } from "@nju/data-access-portal-member";
import { useOfferMode } from "@nju/data-access-portal-offers";
import {
  IMobileOrchestrator,
  IMultiOrderOrchestrator,
  ITvOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import {
  IDataPackage,
  IMobileAccess,
  ITelevision,
  IVoicePackage,
} from "@nju/data-access-portal-product";
import { pushSimpleEvent } from "@nju/data-layer";
import {
  getTotalPrice,
  SimCardRoamingBanner,
} from "@nju/feature-portal-dashboard-components";
import { Icon } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import { A, Panel, Separator, Text } from "@nju/ui";
import { formatDate, formatPrice, parseMegabytesToGigabytes } from "@nju/utils";
import { ChangeMsisdnServiceBanner } from "./ChangeMsisdnServiceBanner";
import { Button, Row, Title } from "./partials";
import { InternationalPackage } from "./partials/InternationalPackage";
import { Promocodes } from "./promocodes";
import { RoamingPackages } from "./RoamingPackages";

interface IMobileOrderCompletedStatusProps {
  orchestrator: IMobileOrchestrator | IMultiOrderOrchestrator | undefined;
  dataPackage: IDataPackage | undefined;
  mobileAccess: IMobileAccess | undefined;
  portoutInProgress: boolean;
  clientNumber: string | undefined;
  voicePackage: IVoicePackage | undefined;
  televisionProducts: ITelevision | undefined;
  tvOrchestrator: ITvOrchestrator | undefined;
  zones: IZone[] | undefined;
}

export function MobileOrderCompletedStatus({
  orchestrator,
  dataPackage,
  mobileAccess,
  portoutInProgress,
  clientNumber,
  voicePackage,
  televisionProducts,
  tvOrchestrator,
  zones,
}: IMobileOrderCompletedStatusProps) {
  const mode = useOfferMode();
  const { reload } = useRouter();
  const isUnsubscribed = mobileAccess?.status === "Unsubscribed";

  const { currentGigabytes, startingGigabytes } = React.useMemo(() => {
    if (!dataPackage?.country) {
      return {
        currentGigabytes: undefined,
        startingGigabytes: undefined,
      };
    }
    const currentGigabytes = parseMegabytesToGigabytes(
      dataPackage.country.currentValue,
      {
        minimumFractionDigits: 2,
      },
    );
    const startingGigabytes = parseMegabytesToGigabytes(
      dataPackage.country.startingValue,
    );

    return {
      currentGigabytes,
      startingGigabytes,
    };
  }, [dataPackage]);

  {
    /* function used interchangeably with birthday package, it will still be used but it uses the same product as birthday and we cannot show both at the same time */
  }
  // const { currentFreeDataPackageGigabytes, startingFreeDataPackageGigabytes } =
  //   React.useMemo(() => {
  //     if (!dataPackage?.freeDataPackage) {
  //       return {
  //         currentFreeDataPackageGigabytes: undefined,
  //         startingFreeDataPackageGigabytes: undefined,
  //       };
  //     }

  //     const currentFreeDataPackageGigabytes = parseMegabytesToGigabytes(
  //       dataPackage.freeDataPackage.balances[0].currentValue,
  //       {
  //         minimumFractionDigits: 2,
  //       }
  //     );
  //     const startingFreeDataPackageGigabytes = parseMegabytesToGigabytes(
  //       dataPackage.freeDataPackage.balances[0].startingValue
  //     );

  //     return {
  //       currentFreeDataPackageGigabytes,
  //       startingFreeDataPackageGigabytes,
  //     };
  //   }, [dataPackage]);

  const { currentBirthdayGigabytes, startingBirthdayGigabytes } =
    React.useMemo(() => {
      if (!dataPackage?.birthdayPackage) {
        return {
          currentBirthdayGigabytes: undefined,
          startingBirthdayGigabytes: undefined,
        };
      }

      const currentBirthdayGigabytes = parseMegabytesToGigabytes(
        dataPackage.birthdayPackage.balances[0].currentValue,
        {
          minimumFractionDigits: 2,
        },
      );
      const startingBirthdayGigabytes = parseMegabytesToGigabytes(
        dataPackage.birthdayPackage.balances[0].startingValue,
      );

      return {
        currentBirthdayGigabytes,
        startingBirthdayGigabytes,
      };
    }, [dataPackage]);

  const handlePushDataLayer = React.useCallback((eventName: string) => {
    pushSimpleEvent({ event: "activity_service", eventName });
  }, []);

  const refresh = React.useCallback(() => {
    reload();
  }, [reload]);

  const isRoamingBarringActive =
    mobileAccess?.characteristic?.fraudRoamingBarring === "Y";
  const isGlobalRoamingBarringActive =
    mobileAccess?.characteristic?.fraudOutgoingBarring === "Y";
  const { changeMsisdn } = useMemberOrchestrator();
  const isChangeServiceCTABannerActive =
    changeMsisdn?.state === "ORDER_FULFILLMENT" ||
    changeMsisdn?.state === "ORDER_COMMITTED";

  const totalPrice = getTotalPrice({
    mobileAccessProducts: mobileAccess,
    tvProducts: televisionProducts,
  });

  const euInternationalCallsZoneName = zones?.find((zone) => {
    return zone.category === "INTERNATIONAL" && zone.name === "EU";
  })?.displayName.pl;
  const stdInternationalCallsZoneName = zones?.find((zone) => {
    return zone.category === "INTERNATIONAL" && zone.name === "STD";
  })?.displayName.pl;
  const exoInternationalCallsZoneName = zones?.find((zone) => {
    return zone.category === "INTERNATIONAL" && zone.name === "EXO";
  })?.displayName.pl;

  return (
    <div className="flex flex-col gap-4">
      {(isGlobalRoamingBarringActive || isRoamingBarringActive) && (
        <SimCardRoamingBanner
          isGlobalRoamingActive={isGlobalRoamingBarringActive}
          isResignation14DaysAllowed={mobileAccess?.resignation14DaysAllowed}
        />
      )}
      {isChangeServiceCTABannerActive ? (
        <ChangeMsisdnServiceBanner orchestrator={changeMsisdn} />
      ) : (
        <Panel testId="mobile-details-service-tab">
          <Title
            clientNumber={clientNumber}
            iconName="mobile"
            testId="client-number"
            title={mode === "postpaid" ? "nju abonament" : "nju subskrypcja"}
          />
          <Separator size="s" />
          {startingGigabytes && currentGigabytes && (
            <>
              <Text
                size={{
                  "@onlyMobile": "s",
                  "@fromTablet": "l",
                }}
              >
                pozostało
              </Text>
              <Separator size="none" tone="invisible" />
              <Text
                size={{
                  "@onlyMobile": "s",
                  "@fromTablet": "l",
                }}
                testId="starting-gigabytes-mv"
              >
                <Text
                  size={{
                    "@onlyMobile": "m",
                    "@fromTablet": "keyM",
                  }}
                  testId="current-gigabytes-mv"
                  weight="bold"
                >
                  {currentGigabytes} GB{" "}
                </Text>
                z {startingGigabytes} GB
              </Text>
            </>
          )}
          {!isGlobalRoamingBarringActive && (
            <>
              <Separator size="xs" tone="invisible" />
              <div className="flex only-mobile:flex-col from-tablet:items-center gap-2">
                <Button
                  iconName="sim-card"
                  onClick={() => {
                    handlePushDataLayer("SIMCard");
                  }}
                  path={portalRoutes.mojeKonto.mojaKarta.index.get({
                    ...(orchestrator?.userId && {
                      userId: orchestrator.userId,
                    }),
                  })}
                  testId="number-and-sim-card-button"
                  title="numer i karta eSIM/SIM"
                />
                <Button
                  iconName="cart-plus"
                  onClick={() => {
                    handlePushDataLayer("additional_services");
                  }}
                  path={portalRoutes.mojeKonto.uslugiDodatkowe.index.get({
                    ...(orchestrator?.userId && {
                      userId: orchestrator.userId,
                    }),
                  })}
                  title="dodatkowe usługi"
                />
              </div>
            </>
          )}
        </Panel>
      )}
      {dataPackage?.birthdayPackage && (
        <Panel testId="mobile-details-service-tab">
          <div className="flex items-center gap-3">
            <Icon
              className="w-5 h-5 from-tablet:w-6 from-tablet:h-6"
              name="gift"
              tone="accent"
            />
            <div className="flex flex-col">
              <Text
                size={{
                  "@onlyMobile": "m",
                  "@fromTablet": "xl",
                }}
                weight="bold"
              >
                promocyjne GB
              </Text>
            </div>
          </div>
          <Separator size="s" />
          {startingBirthdayGigabytes && currentBirthdayGigabytes && (
            <>
              <Text
                size={{
                  "@onlyMobile": "s",
                  "@fromTablet": "l",
                }}
              >
                pozostało
              </Text>
              <Separator size="none" tone="invisible" />
              <Text
                size={{
                  "@onlyMobile": "s",
                  "@fromTablet": "l",
                }}
              >
                <Text
                  size={{
                    "@onlyMobile": "m",
                    "@fromTablet": "keyM",
                  }}
                  weight="bold"
                >
                  {currentBirthdayGigabytes} GB{" "}
                </Text>
                z {startingBirthdayGigabytes} GB
              </Text>
            </>
          )}
          {dataPackage?.birthdayPackage?.expiryDate && (
            <Text className="mt-1" display="block" size="s">
              pakiet ważny do{" "}
              {formatDate(
                dataPackage.birthdayPackage.expiryDate,
                true,
                ".",
                ", ",
              )}
            </Text>
          )}
        </Panel>
      )}
      {/* component used interchangeably with birthday package, it will still be used but it uses the same product as birthday and we cannot show both at the same time */}
      {/* {dataPackage?.freeDataPackage && (
        <Panel testId="mobile-details-service-tab">
          <div className="flex items-center gap-3">
            <Icon
              className="w-5 h-5 from-tablet:w-6 from-tablet:h-6"
              name="gift"
              tone="accent"
            />
            <div className="flex flex-col">
              <Text
                size={{
                  "@onlyMobile": "m",
                  "@fromTablet": "xl",
                }}
                weight="bold"
              >
                darmowe GB
              </Text>
              <BlockText>dodatkowe GB dla Ciebie</BlockText>
            </div>
          </div>
          <Separator size="s" />
          {startingFreeDataPackageGigabytes && currentFreeDataPackageGigabytes && (
            <>
              <Text
                size={{
                  "@onlyMobile": "s",
                  "@fromTablet": "l",
                }}
              >
                pozostało
              </Text>
              <Separator size="none" tone="invisible" />
              <Text
                size={{
                  "@onlyMobile": "s",
                  "@fromTablet": "l",
                }}
              >
                <Text
                  size={{
                    "@onlyMobile": "m",
                    "@fromTablet": "keyM",
                  }}
                  weight="bold"
                >
                  {currentFreeDataPackageGigabytes} GB{" "}
                </Text>
                z {startingFreeDataPackageGigabytes} GB
              </Text>
            </>
          )}
          {dataPackage?.freeDataPackage?.expiryDate && (
            <Text className="mt-1" display="block" size="s">
              pakiet ważny do{" "}
              {formatDate(
                dataPackage.freeDataPackage.expiryDate,
                true,
                ".",
                ", "
              )}
            </Text>
          )}
        </Panel>
      )} */}
      {!isUnsubscribed && (
        <Panel testId="mobile-payment-tab">
          <Title iconName="wallet-mobile" title="najbliższa płatność" />
          <Separator size="s" />
          {!portoutInProgress && (
            <>
              {mobileAccess?.anniversaryDate && (
                <Text
                  size={{
                    "@onlyMobile": "s",
                    "@fromTablet": "m",
                  }}
                  tone="accent"
                >
                  {formatDate(mobileAccess.anniversaryDate, false)}
                </Text>
              )}
              <Separator size="xxs" tone="invisible" />
              {mobileAccess?.recurringPrice !== undefined && (
                <Row
                  testId="mobile-monthly-fee"
                  title={
                    mode === "postpaid"
                      ? "opłata nju abonament"
                      : "opłata nju subskrypcja"
                  }
                  value={`${formatPrice(totalPrice)} PLN`}
                />
              )}
              <Separator size="s" />
              <div className="flex only-mobile:items-start from-tablet:items-center">
                <Text
                  size={{
                    "@onlyMobile": "s",
                    "@fromTablet": "s",
                  }}
                >
                  {`usługa nju ${
                    mode === "postpaid" ? "abonament" : "subskrypcja"
                  } odnawia się automatycznie co miesiąc`}
                </Text>
              </div>
            </>
          )}
          {portoutInProgress && (
            <div className="flex items-center flex-col from-tablet:justify-between">
              <Icon height={90} name="file-incircle" width={90} />
              <Separator size="xxs" tone="invisible" />
              <Text
                size={{
                  "@onlyMobile": "s",
                  "@fromTablet": "l",
                }}
              >
                brak najbliższych płatności
              </Text>
            </div>
          )}
        </Panel>
      )}
      <Promocodes mobileAccess={mobileAccess} />
      <RoamingPackages
        dataPackage={dataPackage}
        orchestrator={orchestrator}
        voicePackage={voicePackage}
        zones={zones}
      />
      {voicePackage?.internationalCalls &&
        voicePackage?.internationalCalls?.length > 0 && (
          <Panel
            iconName="phone-volume"
            isFoldable={true}
            testId="international-tab"
            title="rozmowy międzynarodowe"
          >
            {({ isExpanded }) => {
              return (
                <>
                  <Text
                    size={{
                      "@onlyMobile": "m",
                      "@fromTablet": "l",
                    }}
                  >
                    dodatkowe połączenia między Polską a wybranym krajem
                  </Text>
                  {isExpanded && (
                    <>
                      <InternationalPackage
                        testId="standard-international-tariff-minutes"
                        voicePackage={voicePackage}
                        zone="STD"
                        zoneName={stdInternationalCallsZoneName}
                      />
                      <InternationalPackage
                        testId="exotic-international-tariff-minutes"
                        voicePackage={voicePackage}
                        zone="EXO"
                        zoneName={exoInternationalCallsZoneName}
                      />
                      <InternationalPackage
                        testId="eu-international-tariff-minutes"
                        voicePackage={voicePackage}
                        zone="EU"
                        zoneName={euInternationalCallsZoneName}
                      />
                      <div className="mt-5 flex justify-start">
                        <Button
                          iconName="cart-plus"
                          onClick={() => {
                            handlePushDataLayer("additional_packs");
                          }}
                          path={portalRoutes.mojeKonto.uslugiDodatkowe.rozmowyMiedzynarodowe.index.get(
                            {
                              ...(orchestrator?.userId && {
                                userId: orchestrator.userId,
                              }),
                            },
                          )}
                          title="dodatkowe pakiety"
                        />
                      </div>
                    </>
                  )}
                </>
              );
            }}
          </Panel>
        )}
      {televisionProducts && (
        <Panel
          iconName="tv"
          isFoldable={true}
          testId="tv-offer-panel"
          title="telewizja mobilna"
        >
          {({ isExpanded }) => {
            return (
              <>
                <Text
                  className="from-tablet:max-w-[95%]"
                  display="block"
                  size={{
                    "@onlyMobile": "m",
                    "@fromTablet": "l",
                  }}
                >
                  W pakiecie podstawowym znajdziesz ponad 40 kanałów
                  tematycznych, w tym kanały filmowe, informacyjne, rozrywkowe i
                  muzyczne. Więcej szczegółów na stronie
                  <Link
                    href="https://www.njumobile.pl/oferta/telewizja-mobilna"
                    passHref={true}
                    target="_blank"
                  >
                    <A
                      display="block"
                      size="inline"
                      variant="simple"
                      withUnderline={true}
                    >
                      <span className="only-mobile:text-size-16 from-tablet:text-size-20">
                        njumobile.pl/oferta/telewizja-mobilna.
                      </span>
                    </A>
                  </Link>
                </Text>
                {isExpanded && (
                  <>
                    <Separator size="s" />
                    {(!tvOrchestrator?.state ||
                      tvOrchestrator?.userId !== orchestrator?.userId) &&
                      !televisionProducts.characteristic?.login && (
                        <>
                          <Text
                            size={{
                              "@onlyMobile": "m",
                              "@fromTablet": "l",
                            }}
                            weight="bold"
                          >
                            Aby korzystać z usługi, załóż konto. Zacznij od
                            stworzenia swojego kodu poufnego.
                          </Text>
                          <Separator size="xs" tone="invisible" />
                          <div className="flex only-mobile:flex-col from-tablet:items-center gap-2">
                            <Button
                              iconName="user"
                              path={portalRoutes.mojeKonto.dodajKodPoufny.get({
                                mode: "mobile",
                                ...(orchestrator?.userId && {
                                  userId: orchestrator.userId,
                                }),
                              })}
                              testId="number-and-sim-card-button"
                              title="załóż konto"
                            />
                          </div>
                        </>
                      )}
                    {tvOrchestrator?.state &&
                      tvOrchestrator.userId === orchestrator?.userId &&
                      !televisionProducts.characteristic?.login && (
                        <>
                          <div className="flex items-center gap-2">
                            <Icon
                              height={20}
                              name="info-circle"
                              tone="accent"
                              width={20}
                            />
                            <Text size="m">
                              Rejestracja w trakcie. Wkrótce zobaczysz tutaj
                              swój login.
                              <A
                                display="inline"
                                onClick={refresh}
                                size="inline"
                                variant="simple"
                              >
                                <Text
                                  className="ml-2"
                                  size={{
                                    "@onlyMobile": "s",
                                    "@fromTablet": "m",
                                  }}
                                  tone="accent"
                                  weight="bold"
                                  withUnderline={true}
                                >
                                  odśwież
                                </Text>
                              </A>
                            </Text>
                          </div>
                        </>
                      )}
                    {televisionProducts.characteristic?.login && (
                      <>
                        <div className="flex flex-col">
                          <Text
                            size={{
                              "@onlyMobile": "m",
                              "@fromTablet": "l",
                            }}
                          >
                            Nazwa użytkownika
                          </Text>
                          <Text
                            size={{
                              "@onlyMobile": "m",
                              "@fromTablet": "l",
                            }}
                            weight="bold"
                          >
                            {televisionProducts.characteristic.login}
                          </Text>
                        </div>
                        <Separator size="xs" tone="invisible" />
                        <div className="flex flex-col">
                          <Text
                            size={{
                              "@onlyMobile": "m",
                              "@fromTablet": "l",
                            }}
                          >
                            kod poufny
                          </Text>
                          <div className="flex flex-row">
                            <Text
                              size={{
                                "@onlyMobile": "m",
                                "@fromTablet": "l",
                              }}
                              testId="user-tv-go-name"
                              weight="bold"
                            >
                              ****
                            </Text>
                            <Link
                              href={portalRoutes.mojeKonto.zmienKodPoufny.get()}
                              passHref={true}
                            >
                              <A
                                display="inline"
                                size="inline"
                                variant="simple"
                              >
                                <Text
                                  className="ml-2"
                                  size={{
                                    "@onlyMobile": "s",
                                    "@fromTablet": "m",
                                  }}
                                  tone="accent"
                                  weight="bold"
                                  withUnderline={true}
                                >
                                  zmień
                                </Text>
                              </A>
                            </Link>
                          </div>
                        </div>
                        <Separator size="xs" tone="invisible" />
                        <div className="flex only-mobile:flex-col from-tablet:items-center gap-2">
                          <Button
                            iconName="tv"
                            path="https://tvgo.orange.pl"
                            target="blank"
                            title="przejdź do telewizji mobilnej"
                          />
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            );
          }}
        </Panel>
      )}
    </div>
  );
}
